import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, Mic, Loader2, Trash2, Pencil, Check, X,
  Archive, ArchiveRestore, Plus,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const authHdr = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const CATEGORY_STYLES = {
  board: "bg-purple-900/50 text-purple-300 border-purple-700",
  committee: "bg-blue-900/50 text-blue-300 border-blue-700",
  uncategorized: "bg-slate-700/60 text-slate-300 border-slate-600",
};

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch (e) {
    return iso;
  }
};

export default function ScriptlyNotes() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("active");
  const [category, setCategory] = useState("");
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [newText, setNewText] = useState("");
  const [newCategory, setNewCategory] = useState("board");

  const loadNotes = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ status: tab });
    if (category) params.set("category", category);
    axios
      .get(`${BACKEND_URL}/api/scriptly/notes?${params}`, authHdr())
      .then((res) => setNotes(res.data.notes || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load notes"))
      .finally(() => setLoading(false));
  }, [tab, category]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  const patchNote = async (id, body, msg) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/scriptly/notes/${id}`, body, authHdr());
      toast.success(msg);
      loadNotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const deleteNote = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/scriptly/notes/${id}`, authHdr());
      toast.success("Note deleted");
      loadNotes();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const addNote = async () => {
    if (!newText.trim()) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/scriptly/notes`,
        { text: newText.trim(), category: newCategory }, authHdr()
      );
      toast.success("Note added");
      setNewText("");
      setShowAdd(false);
      loadNotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Add failed");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6">
      <div className="max-w-4xl mx-auto space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/")}
            className="border-slate-600 bg-slate-800 text-white hover:bg-slate-700" data-testid="scriptly-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
              <Mic className="w-6 h-6 text-purple-400" /> Scriptly Notes
            </h1>
            <p className="text-slate-400 text-sm">
              Meeting notes captured by voice — say "Hey Scriptly" in a voice channel after <code className="text-slate-300">/scriptly join</code>
            </p>
          </div>
          <Button size="sm" onClick={() => setShowAdd(!showAdd)}
            className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="scriptly-add-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Note
          </Button>
        </div>

        {showAdd && (
          <Card className="bg-slate-800/70 border-slate-700" data-testid="scriptly-add-form">
            <CardContent className="p-4 space-y-3">
              <Textarea value={newText} onChange={(e) => setNewText(e.target.value)}
                placeholder="Note text…" className="bg-slate-900 border-slate-700 text-white"
                data-testid="scriptly-add-text" />
              <div className="flex flex-wrap items-center gap-2">
                {["board", "committee", "uncategorized"].map((c) => (
                  <button key={c} onClick={() => setNewCategory(c)}
                    data-testid={`scriptly-add-cat-${c}`}
                    className={`px-3 py-1 rounded-full text-xs border capitalize ${
                      newCategory === c ? CATEGORY_STYLES[c] : "border-slate-700 text-slate-500"
                    }`}>
                    {c}
                  </button>
                ))}
                <div className="flex-1" />
                <Button size="sm" onClick={addNote} className="bg-green-600 hover:bg-green-700"
                  data-testid="scriptly-add-save">Save</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-lg overflow-hidden border border-slate-700">
            {[["active", "Notes"], ["tabled", "Tabled"]].map(([v, label]) => (
              <button key={v} onClick={() => setTab(v)} data-testid={`scriptly-tab-${v}`}
                className={`px-4 py-1.5 text-sm ${
                  tab === v ? "bg-purple-600 text-white" : "bg-slate-800 text-slate-400 hover:text-white"
                }`}>
                {label}
              </button>
            ))}
          </div>
          <div className="flex gap-1.5">
            {["", "board", "committee", "uncategorized"].map((c) => (
              <button key={c || "all"} onClick={() => setCategory(c)}
                data-testid={`scriptly-filter-${c || "all"}`}
                className={`px-3 py-1 rounded-full text-xs border capitalize ${
                  category === c
                    ? c ? CATEGORY_STYLES[c] : "bg-slate-600 text-white border-slate-500"
                    : "border-slate-700 text-slate-500 hover:text-slate-300"
                }`}>
                {c || "All"}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
          </div>
        ) : notes.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="py-12 text-center text-slate-400" data-testid="scriptly-empty">
              {tab === "tabled"
                ? "No tabled notes. Say \"Hey Scriptly… table the last note\" in a meeting."
                : "No notes yet. In a voice channel, run /scriptly join and say \"Hey Scriptly\"."}
            </CardContent>
          </Card>
        ) : (
          notes.map((n) => (
            <Card key={n.id} className="bg-slate-800/50 border-slate-700" data-testid={`scriptly-note-${n.id}`}>
              <CardContent className="p-4 space-y-2">
                {editing === n.id ? (
                  <div className="space-y-2">
                    <Textarea value={editText} onChange={(e) => setEditText(e.target.value)}
                      className="bg-slate-900 border-slate-700 text-white" data-testid="scriptly-edit-text" />
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-green-600 hover:bg-green-700"
                        data-testid="scriptly-edit-save"
                        onClick={() => { patchNote(n.id, { text: editText }, "Note updated"); setEditing(null); }}>
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing(null)}
                        className="border-slate-600 text-slate-300">
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-white">{n.text}</p>
                )}
                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
                  <Badge className={CATEGORY_STYLES[n.category] || CATEGORY_STYLES.uncategorized}>
                    {n.category}
                  </Badge>
                  {n.source === "voice" && (
                    <Badge className="bg-slate-700/60 text-slate-300 border-slate-600">
                      <Mic className="w-3 h-3 mr-1" /> voice
                    </Badge>
                  )}
                  <span>{n.spoken_by ? `by ${n.spoken_by}` : ""}</span>
                  <span>· {fmt(n.created_at)}</span>
                  {n.status === "tabled" && n.tabled_at && <span>· tabled {fmt(n.tabled_at)}</span>}
                  <div className="flex-1" />
                  <div className="flex gap-1">
                    <button title="Change category" data-testid={`scriptly-cycle-cat-${n.id}`}
                      onClick={() => {
                        const order = ["board", "committee", "uncategorized"];
                        const next = order[(order.indexOf(n.category) + 1) % order.length];
                        patchNote(n.id, { category: next }, `Moved to ${next}`);
                      }}
                      className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-blue-300 text-[10px] uppercase font-bold border border-slate-700">
                      {n.category === "board" ? "B" : n.category === "committee" ? "C" : "U"}
                    </button>
                    <button title="Edit" data-testid={`scriptly-edit-${n.id}`}
                      onClick={() => { setEditing(n.id); setEditText(n.text); }}
                      className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-white">
                      <Pencil className="w-4 h-4" />
                    </button>
                    {n.status === "active" ? (
                      <button title="Table this note" data-testid={`scriptly-table-${n.id}`}
                        onClick={() => patchNote(n.id, { status: "tabled" }, "Note tabled")}
                        className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-amber-300">
                        <Archive className="w-4 h-4" />
                      </button>
                    ) : (
                      <button title="Untable (restore)" data-testid={`scriptly-untable-${n.id}`}
                        onClick={() => patchNote(n.id, { status: "active" }, "Note restored")}
                        className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-green-300">
                        <ArchiveRestore className="w-4 h-4" />
                      </button>
                    )}
                    <button title="Delete" data-testid={`scriptly-delete-${n.id}`}
                      onClick={() => deleteNote(n.id)}
                      className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-red-400">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
